import type { ChartType, RangeKey } from "@chartandgo/shared";
import {
  GRID_COLS,
  MIN_CARD_H,
  MIN_CARD_W,
  type ChartCard,
  type GridItem,
  type StockBasket,
} from "../store/workspace.js";

export const BASKET_TRANSFER_VERSION = 1;
const MAX_CARDS = 50;
const MAX_FILE_BYTES = 100_000;
const VALID_RANGES: RangeKey[] = [
  "1D",
  "5D",
  "1M",
  "3M",
  "6M",
  "YTD",
  "1Y",
  "5Y",
  "MAX",
];
const VALID_TYPES: ChartType[] = ["candlestick", "line", "area", "bar"];

export interface BasketSnapshotCard {
  symbol: string;
  name: string;
  range: RangeKey;
  chartType: ChartType;
  showVolume: boolean;
  placement: Omit<GridItem, "i">;
}

export interface BasketSnapshot {
  kind: "chartandgo-basket";
  version: typeof BASKET_TRANSFER_VERSION;
  name: string;
  cards: BasketSnapshotCard[];
}

export function snapshotBasket(basket: StockBasket): BasketSnapshot {
  if (basket.cards.length > MAX_CARDS) {
    throw new Error(`A shared basket can contain at most ${MAX_CARDS} charts.`);
  }

  return {
    kind: "chartandgo-basket",
    version: BASKET_TRANSFER_VERSION,
    name: basket.name,
    cards: basket.cards.map((card) => {
      const placement = basket.layout.find((item) => item.i === card.id);
      if (!placement) {
        throw new Error(`The ${card.symbol} chart is missing its layout.`);
      }
      return {
        symbol: card.symbol,
        name: card.name,
        range: card.range,
        chartType: card.chartType,
        showVolume: card.showVolume,
        placement: {
          x: placement.x,
          y: placement.y,
          w: placement.w,
          h: placement.h,
        },
      };
    }),
  };
}

export function serializeBasket(basket: StockBasket): string {
  return JSON.stringify(snapshotBasket(basket), null, 2);
}

export function parseBasketSnapshot(raw: string): BasketSnapshot {
  if (new TextEncoder().encode(raw).byteLength > MAX_FILE_BYTES) {
    throw new Error("This basket file is too large.");
  }

  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    throw new Error("This is not a valid ChartAndGo basket file.");
  }

  if (!isRecord(value) || value.kind !== "chartandgo-basket") {
    throw new Error("This file is not a ChartAndGo basket.");
  }
  if (value.version !== BASKET_TRANSFER_VERSION) {
    throw new Error("This basket was created by an unsupported app version.");
  }
  if (
    typeof value.name !== "string" ||
    !value.name.trim() ||
    value.name.length > 40
  ) {
    throw new Error("The basket name is invalid.");
  }
  if (!Array.isArray(value.cards) || value.cards.length > MAX_CARDS) {
    throw new Error(`A basket can contain at most ${MAX_CARDS} charts.`);
  }

  const cards = value.cards.map(parseSnapshotCard);
  return {
    kind: "chartandgo-basket",
    version: BASKET_TRANSFER_VERSION,
    name: value.name.trim(),
    cards,
  };
}

export function basketFromSnapshot(
  snapshot: BasketSnapshot,
  makeId: () => string = createId,
): StockBasket {
  const basketId = makeId();
  const cards: ChartCard[] = [];
  const layout: GridItem[] = [];

  for (const item of snapshot.cards) {
    const id = makeId();
    cards.push({
      id,
      symbol: item.symbol,
      name: item.name,
      range: item.range,
      chartType: item.chartType,
      showVolume: item.showVolume,
    });
    layout.push({ i: id, ...item.placement });
  }

  return {
    id: basketId,
    name: snapshot.name,
    cards,
    layout,
  };
}

export function duplicateBasket(
  basket: StockBasket,
  makeId: () => string = createId,
): StockBasket {
  return basketFromSnapshot(
    {
      ...snapshotBasket(basket),
      name: `${basket.name} copy`.slice(0, 40),
    },
    makeId,
  );
}

export function encodeBasketSharePayload(basket: StockBasket): string {
  const bytes = new TextEncoder().encode(JSON.stringify(snapshotBasket(basket)));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

export function decodeBasketSharePayload(payload: string): BasketSnapshot {
  if (payload.length > 150_000) {
    throw new Error("This basket link is too large.");
  }
  if (!/^[A-Za-z0-9_-]+$/.test(payload)) {
    throw new Error("This basket link is invalid.");
  }

  try {
    const base64 = payload.replaceAll("-", "+").replaceAll("_", "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return parseBasketSnapshot(new TextDecoder().decode(bytes));
  } catch (error) {
    if (error instanceof Error && error.message.startsWith("This ")) {
      throw error;
    }
    throw new Error("This basket link is invalid.");
  }
}

function parseSnapshotCard(value: unknown): BasketSnapshotCard {
  if (!isRecord(value)) throw new Error("A basket chart is invalid.");
  if (
    typeof value.symbol !== "string" ||
    !/^[A-Za-z.-]{1,12}$/.test(value.symbol)
  ) {
    throw new Error("A basket contains an invalid stock symbol.");
  }
  if (
    typeof value.name !== "string" ||
    !value.name.trim() ||
    value.name.length > 200
  ) {
    throw new Error(`The ${value.symbol} chart name is invalid.`);
  }
  if (!VALID_RANGES.includes(value.range as RangeKey)) {
    throw new Error(`The ${value.symbol} chart range is invalid.`);
  }
  if (!VALID_TYPES.includes(value.chartType as ChartType)) {
    throw new Error(`The ${value.symbol} chart type is invalid.`);
  }
  if (typeof value.showVolume !== "boolean") {
    throw new Error(`The ${value.symbol} volume setting is invalid.`);
  }
  if (!isRecord(value.placement) || !isValidPlacement(value.placement)) {
    throw new Error(`The ${value.symbol} chart placement is invalid.`);
  }

  return {
    symbol: value.symbol.toUpperCase(),
    name: value.name.trim(),
    range: value.range as RangeKey,
    chartType: value.chartType as ChartType,
    showVolume: value.showVolume,
    placement: {
      x: Math.round(value.placement.x),
      y: Math.round(value.placement.y),
      w: Math.round(value.placement.w),
      h: Math.round(value.placement.h),
    },
  };
}

function isValidPlacement(value: Record<string, unknown>): value is {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const { x, y, w, h } = value;
  return (
    typeof x === "number" &&
    Number.isFinite(x) &&
    Number.isInteger(x) &&
    x >= 0 &&
    typeof y === "number" &&
    Number.isFinite(y) &&
    Number.isInteger(y) &&
    y >= 0 &&
    typeof w === "number" &&
    Number.isFinite(w) &&
    Number.isInteger(w) &&
    w >= MIN_CARD_W &&
    w <= GRID_COLS &&
    typeof h === "number" &&
    Number.isFinite(h) &&
    Number.isInteger(h) &&
    h >= MIN_CARD_H &&
    x + w <= GRID_COLS
  );
}

function createId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `basket-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
